<template>
    <div class="card">
        <div v-if="cardheader" class="card-header bg-light"><h5 class="card-title">{{ cardheader }}</h5></div>
        <div class="card-header bg-light" style="padding: 5px 20px;">
            <div v-if="editor" class="ste-menubar">
                <button
                    v-if="toolbar.includes('h1') && !rawmode"
                    type="button"
                    class="ste-menubar__button"
                    :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
                    @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                >
                    H1
                </button>
                <button
                    v-if="toolbar.includes('h2') && !rawmode"
                    type="button"
                    class="ste-menubar__button"
                    :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                    @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                >
                    H2
                </button>
                <button
                    v-if="toolbar.includes('h3') && !rawmode"
                    type="button"
                    class="ste-menubar__button"
                    :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
                    @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                >
                    H3
                </button>

                <div class="ste-menubar__divider" v-if="!rawmode && (toolbar.includes('h1') || toolbar.includes('h2') || toolbar.includes('h3'))"></div>

                <button
                    v-if="toolbar.includes('bold') && !rawmode"
                    type="button"
                    class="ste-menubar__button"
                    :class="{ 'is-active': editor.isActive('bold') }"
                    @click="editor.chain().focus().toggleBold().run()"
                >
                    <i class="icon-bold2"></i>
                </button>
                <button
                    v-if="toolbar.includes('italic') && !rawmode"
                    type="button"
                    class="ste-menubar__button"
                    :class="{ 'is-active': editor.isActive('italic') }"
                    @click="editor.chain().focus().toggleItalic().run()"
                >
                    <i class="icon-italic2"></i>
                </button>
                <button
                    v-if="toolbar.includes('strike') && !rawmode"
                    type="button"
                    class="ste-menubar__button"
                    :class="{ 'is-active': editor.isActive('strike') }"
                    @click="editor.chain().focus().toggleStrike().run()"
                >
                    <i class="icon-strikethrough3"></i>
                </button>
                <button
                    v-if="toolbar.includes('underline') && !rawmode"
                    type="button"
                    class="ste-menubar__button"
                    :class="{ 'is-active': editor.isActive('underline') }"
                    @click="editor.chain().focus().toggleUnderline().run()"
                >
                    <i class="icon-underline2"></i>
                </button>

                <div class="ste-menubar__divider" v-if="!rawmode && toolbar.includes('textalign')"></div>

                <div class="btn-group">
                    <button
                        v-if="!rawmode && toolbar.includes('textalign')"
                        type="button"
                        class="ste-menubar__button"
                        :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
                        @click="editor.chain().focus().setTextAlign('left').run()"
                    >
                        <i class="icon-paragraph-left3"></i>
                    </button>
                    <button
                        v-if="!rawmode && toolbar.includes('textalign')"
                        type="button"
                        class="ste-menubar__button"
                        :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
                        @click="editor.chain().focus().setTextAlign('center').run()"
                    >
                        <i class="icon-paragraph-center3"></i>
                    </button>
                    <button
                        v-if="!rawmode && toolbar.includes('textalign')"
                        type="button"
                        class="ste-menubar__button"
                        :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
                        @click="editor.chain().focus().setTextAlign('right').run()"
                    >
                        <i class="icon-paragraph-right3"></i>
                    </button>
                </div>

                <div class="ste-menubar__divider" v-if="!rawmode && (toolbar.includes('undo') || toolbar.includes('redo'))"></div>

                <button
                    v-if="!rawmode && toolbar.includes('undo')"
                    type="button"
                    class="ste-menubar__button"
                    @click="editor.commands.undo"
                >
                    <i class="icon-undo"></i>
                </button>
                <button
                    v-if="!rawmode && toolbar.includes('redo')"
                    type="button"
                    class="ste-menubar__button"
                    @click="editor.commands.redo"
                >
                    <i class="icon-redo"></i>
                </button>

                <slot name="toolbar" :editor="editor"></slot>

                <button
                    v-if="toolbar.includes('rawmode')"
                    type="button"
                    class="ste-menubar__button ml-auto"
                    @click="rawmode = !rawmode"
                >
                    <i class="icon-embed2"></i>
                </button>
            </div>
        </div>

        <div v-if="rawmode" class="card-body">
            <textarea
                ref="textarea"
                class="ste-elastic_textarea"
                :class="textClass"
                v-model="html"
                @input="resize"
                @focus="resize"
            ></textarea>
        </div>
        <editor-content v-else class="card-body ste-editor__content" :editor="editor" :class="textClass" />
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { debounce, uniqueId } from 'lodash';

export default {
    name: 'SimpleTextEditor',
    components: {
        EditorContent,
    },
    props: {
        cardheader: {
            type: String,
            default: '',
        },
        value: null,
        toolbar: {
            type: Array,
            default() {
                return [
                    'bold',
                    'italic',
                    'strike',
                    'underline',
                ];
            },
        },
        textClass: {
            type: [String, Object, Array],
            default: '',
        },
    },
    data: () => ({
        busy_saving: false,
        editor: null,
        component_id: '99',
        rawmode: false,
        html: '',
    }),
    created() {
        this.component_id = uniqueId();
    },
    mounted() {
        this.editor = new Editor({
            extensions: [
                StarterKit.configure({
                    heading: {
                        levels: [1, 2, 3, 4],
                    },
                    blockquote: {
                        HTMLAttributes: {
                            class: 'blockquote',
                        },
                    },
                }),
                Underline,
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
            ],
            onUpdate: debounce(({ editor }) => {
                this.emitData(editor.getHTML());
            }, 500),
        });
        this.editor.commands.setContent(this.value, true);
    },
    watch: {
        value(val) {
            if (this.rawmode) {
                const isSame = this.html === val;
                if (isSame) {
                    return;
                }
                this.html = val;
            } else {
                const isSame = this.editor.getHTML() === val;
                if (isSame) {
                    return;
                }
                this.editor.commands.setContent(val, false);
            }
        },
        html: debounce(function debounceHtml(html) {
            const vm = this;
            vm.resize();
            vm.emitData(html);
        }, 500),
        rawmode(val) {
            if (val) {
                this.html = this.editor.getHTML();
            } else {
                this.editor.commands.setContent(this.html, false);
            }
        },
    },
    beforeDestroy() {
        this.editor.destroy();
    },
    methods: {
        debounce,
        uniqueId,
        emitData(content) {
            this.$emit('input', content);
        },
        resize() {
            const { textarea } = this.$refs;
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + textarea.offsetHeight - textarea.clientHeight + 'px';
        },
    },
};
</script>
<style>
.ste-menubar {
    display: flex;
    flex-direction: row;
}
.ste-menubar__divider {
    padding: 6px 9px;
    display: inline-block;
}
.ste-menubar__divider:before {
    content: '';
    border-right: 1px solid #ddd;
}
.ste-menubar__button {
    background-color: transparent;
    color: #000;
    cursor: pointer;
    display: inline-block;
    font-weight: 400;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    padding: 6px 9px;
    font-size: 0.8125rem;
    line-height: 1.5385;
    border-radius: 0.1875rem;
    border: 0;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.ste-menubar__button.is-active {
    background-color: rgba(0,0,0,.05);
}
.ste-menubar__button:hover {
    background-color: rgba(0,0,0,.05);
}
.ste-editor__content {
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word
}

.ste-editor__content * {
    caret-color: currentColor
}

.ste-editor__content pre {
    padding: .7rem 1rem;
    border-radius: 5px;
    background: #000;
    color: #fff;
    font-size: .8rem;
    overflow-x: auto
}

.ste-editor__content pre code {
    display: block
}

.ste-editor__content p code {
    padding: .2rem .4rem;
    border-radius: 5px;
    font-size: .8rem;
    font-weight: 700;
    background: rgba(0, 0, 0, .1);
    color: rgba(0, 0, 0, .8)
}

.ste-editor__content ol,
.ste-editor__content ul {
    padding-left: 1rem
}

.ste-editor__content li > ol,
.ste-editor__content li > p,
.ste-editor__content li > ul {
    margin: 0
}

.ste-editor__content a {
    color: inherit
}

.ste-editor__content blockquote {
    border-left: 3px solid rgba(0, 0, 0, .1);
    color: rgba(0, 0, 0, .8);
    padding-left: .8rem;
    font-style: italic
}

.ste-editor__content blockquote p {
    margin: 0
}

.ste-editor__content img {
    max-width: 100%;
    border-radius: 3px
}

.ste-editor__content table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden
}

.ste-editor__content table td,
.ste-editor__content table th {
    min-width: 1em;
    border: 2px solid #ddd;
    padding: 3px 5px;
    vertical-align: top;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    position: relative
}

.ste-editor__content table td>*,
.ste-editor__content table th>* {
    margin-bottom: 0
}

.ste-editor__content table th {
    font-weight: 700;
    text-align: left
}

.ste-editor__content table .selectedCell:after {
    z-index: 2;
    position: absolute;
    content: "";
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(200, 200, 255, .4);
    pointer-events: none
}

.ste-editor__content table .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: 0;
    width: 4px;
    z-index: 20;
    background-color: #adf;
    pointer-events: none
}

.ste-editor__content .tableWrapper {
    margin: 1em 0;
    overflow-x: auto
}

.ste-editor__content .resize-cursor {
    cursor: ew-resize;
}

.ste-editor__content .ProseMirror {
    outline: none;
}
.ste-elastic_textarea {
    resize: none;
    box-sizing: border-box;
    overflow: hidden;
    min-height: 60px;
    border: 0;
    width: 100%;
    outline: none;
}
</style>
